import { useMemo } from "react";
import { View, Flex, Text } from "@adobe/react-spectrum";
import "./index.css";
import { formatTime, getCallIcon, getInitialsAndColor } from "../../../utils";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { SpeedDialData } from "../../../types/generic.types";
import { Call, CallParticipant } from "../../../types/call.type";

interface ICallHistorySidebarProps {
  activeCallerId: string;
  getActiveCallId: (id: string) => void;
  callList: CallParticipant[];
}

const CallHistorySidebar = (props: ICallHistorySidebarProps) => {
  const { activeCallerId, getActiveCallId, callList } = props;
  const speedDials = useSelector(
    (state: RootState) => state.history.speedDials
  );

  // Precompute initials and background colors for all calls
  const computedCallList = useMemo(() => {
    return callList?.map((call: Call) => {
      const customerAttributes = call.customer?.attributes || {};
      const customerIdentifier =
        call.customer?.displayName ||
        call.customer?.phoneNumber ||
        customerAttributes.Customer_ANI ||
        "-";

      // Check if quickConnectName is available first
      if ((call.self as any)?.quickConnectName) {
        return {
          ...call,
          initialsData: getInitialsAndColor((call.self as any).quickConnectName),
          displayName: (call.self as any).quickConnectName,
        };
      }

      // Check if the customerIdentifier exists in speedDial
      const speedDialEntry = speedDials.find(
        (entry: SpeedDialData) => entry.phoneNumber === customerIdentifier
      );

      if (speedDialEntry) {
        const fullName = `${speedDialEntry.firstName} ${speedDialEntry.lastName}`;
        return {
          ...call,
          initialsData: getInitialsAndColor(fullName),
          displayName: fullName,
        };
      }

      const firstName = customerAttributes.FIRSTNAME;
      const lastName = customerAttributes.LASTNAME;

      if (firstName && lastName) {
        const fullName = `${firstName} ${lastName}`;
        return {
          ...call,
          initialsData: getInitialsAndColor(fullName),
          displayName: fullName,
        };
      }

      return {
        ...call,
        initialsData: getInitialsAndColor("N A"),
        displayName: customerIdentifier,
      };
    });
  }, [callList, speedDials]);

  return (
    <View
      UNSAFE_className="call-history-sidebar"
      borderTopColor={"gray-200"}
      borderTopWidth={"thick"}
      height={"100%"}
    >
      {computedCallList?.length > 0 &&
        computedCallList.map((call: any, index: number) => {
          const { connectedTime, endTime, missed } = call?.self || {};
          const { formattedConnectedTime, formattedDifference } = formatTime(
            connectedTime,
            endTime
          );
          const {
            initialsData: { initials, backgroundColor },
            displayName,
          } = call;

          const key =
            call?.self?.id ||
            call?.id ||
            `${displayName}-${connectedTime || ""}-${endTime || ""}-${index}`;

          return (
            <div
              key={key}
              onClick={() => getActiveCallId(call?.self?.id)}
              className="call-history-sidebar-list"
            >
              <Flex
                flexGrow={1}
                justifyContent={"space-between"}
                direction={"column"}
                UNSAFE_className={`call-history-sidebar-list-item ${
                  call?.self?.id === activeCallerId
                    ? " call-history-sidebar-list-active"
                    : ""
                }`}
              >
                <Flex>
                  <Flex UNSAFE_style={{ paddingRight: "12px" }}>
                    <View UNSAFE_className="call-history-avatar">
                      <Flex
                        justifyContent="center"
                        alignItems="center"
                        UNSAFE_className="agent-avatar"
                        UNSAFE_style={{
                          backgroundColor,
                          borderRadius: "50%",
                        }}
                      >
                        <Text UNSAFE_className="call-monitor-agent-text">
                          {initials}
                        </Text>
                      </Flex>
                    </View>
                  </Flex>
                  <Flex
                    width={"100%"}
                    alignItems={"end"}
                    justifyContent={"space-between"}
                  >
                    <Flex
                      direction="column"
                      alignItems="start"
                      UNSAFE_className="call-detail-history-list"
                      minWidth="0"
                      flex="1"
                    >
                      <Text UNSAFE_className="call-history-title">
                        {displayName}
                      </Text>
                      <Text
                        UNSAFE_style={{ textAlign: "start", color: "#D7373F" }}
                      >
                        <Flex
                          UNSAFE_className={`call-history-subtitle ${
                            missed ? "call-missed" : "call-attend"
                          }`}
                          alignItems={"center"}
                          justifyContent={"center"}
                          marginTop={5}
                        >
                          <img
                            src={getCallIcon(call)}
                            className="call-history-icon"
                            alt="Missed call icon"
                          />
                          <Text
                            marginX={5}
                            UNSAFE_style={{ whiteSpace: "nowrap" }}
                          >
                            {formattedConnectedTime}
                          </Text>
                        </Flex>
                      </Text>
                    </Flex>
                    <Flex direction="column" alignItems={"end"}>
                      {formattedDifference !== "0s" && (
                        <Text UNSAFE_className="call-history-time-diff">
                          {formattedDifference}
                        </Text>
                      )}
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
            </div>
          );
        })}
    </View>
  );
};

export default CallHistorySidebar;
